import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatRp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GlassCard from "@/components/GlassCard";
import CreateServerWizard from "@/components/CreateServerWizard";
import MidtransModal from "@/components/MidtransModal";
import { Button } from "@/components/ui/button";
import { Server, Cpu, HardDrive, MemoryStick, Plus, Wallet, Zap, ExternalLink } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <GlassCard className="p-5" hover data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center">
          <Icon className="text-cyan-400" size={20} />
        </div>
      </div>
      <div className="mt-4 text-3xl font-heading font-extrabold text-white">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}{sub && <span className="text-slate-600"> · {sub}</span>}</div>
    </GlassCard>
  );
}

export default function Dashboard() {
  const { user, refresh: refreshUser } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);

  const stats = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data });
  const servers = useQuery({ queryKey: ["servers"], queryFn: async () => (await api.get("/servers")).data });
  const plans = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });
  const eggs = useQuery({ queryKey: ["eggs"], queryFn: async () => (await api.get("/eggs")).data });
  const locations = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });

  const onCreated = () => { stats.refetch(); servers.refetch(); refreshUser(); };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Kelola server Minecraft kamu dengan mudah.</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setTopupOpen(true)} variant="outline"
            className="border-cyan-500/30 bg-slate-900/60 text-cyan-300 hover:bg-cyan-600 hover:text-white" data-testid="topup-btn">
            <Wallet size={16} className="mr-2" /> Top Up
          </Button>
          <Button onClick={() => setWizardOpen(true)} className="btn-cyan text-white border-0" data-testid="create-server-btn">
            <Plus size={16} className="mr-2" /> Buat Server
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MemoryStick} label="Total RAM" value={`${stats.data?.ram_used ?? 0}GB`} testid="stat-ram" />
        <StatCard icon={Cpu} label="vCore Aktif" value={stats.data?.cpu_used ?? 0} testid="stat-cpu" />
        <StatCard icon={HardDrive} label="Storage" value={`${stats.data?.disk_used ?? 0}GB`} testid="stat-disk" />
        <StatCard icon={Server} label="Server Aktif" value={stats.data?.servers ?? 0} testid="stat-servers" />
      </div>

      <GlassCard className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl btn-cyan flex items-center justify-center"><Zap className="text-white" /></div>
          <div>
            <div className="text-xs uppercase tracking-wider text-cyan-400">Saldo Kredit</div>
            <div className="font-heading text-2xl font-extrabold text-white">{formatRp(user?.credits)}</div>
          </div>
        </div>
        <Button onClick={() => setTopupOpen(true)} className="btn-cyan text-white border-0" data-testid="dashboard-topup-btn">
          <Wallet size={16} className="mr-2" /> Tambah Saldo
        </Button>
      </GlassCard>

      <div>
        <h2 className="font-heading text-2xl font-bold text-white mb-4">Server Saya</h2>
        {servers.data?.length ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {servers.data.map((s) => <ServerCard key={s.id} s={s} />)}
          </div>
        ) : (
          <GlassCard className="p-10 text-center" data-testid="no-servers">
            <Server className="mx-auto text-slate-600 mb-3" size={40} />
            <p className="text-slate-400">Belum ada server. Buat server pertamamu sekarang!</p>
            <Button onClick={() => setWizardOpen(true)} className="btn-cyan text-white border-0 mt-4">
              <Plus size={16} className="mr-2" /> Buat Server
            </Button>
          </GlassCard>
        )}
      </div>

      <CreateServerWizard open={wizardOpen} onOpenChange={setWizardOpen}
        plans={plans.data || []} eggs={eggs.data || []} locations={locations.data || []} onCreated={onCreated} />
      <MidtransModal open={topupOpen} onOpenChange={setTopupOpen} onPaid={() => { refreshUser(); stats.refetch(); }} />
    </div>
  );
}

const STATUS = {
  installing: { c: "text-amber-400 bg-amber-500/10", d: "bg-amber-400", t: "Installing" },
  pending: { c: "text-amber-400 bg-amber-500/10", d: "bg-amber-400", t: "Pending" },
  failed: { c: "text-rose-400 bg-rose-500/10", d: "bg-rose-400", t: "Gagal" },
  running: { c: "text-emerald-400 bg-emerald-500/10", d: "bg-emerald-400 pulse-online", t: "Online" },
};

export function ServerCard({ s }) {
  const st = STATUS[s.status] || STATUS.pending;
  return (
    <GlassCard className="p-5" hover data-testid={`server-card-${s.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-heading font-bold text-white text-lg">{s.name}</div>
          <div className="text-xs text-slate-500">{s.egg?.name}</div>
        </div>
        <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${st.c}`}>
          <span className={`w-2 h-2 rounded-full ${st.d}`} /> {st.t}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        <div className="p-2 rounded-lg bg-slate-900/50"><div className="text-sm font-semibold text-cyan-300">{s.plan?.ram_gb}GB</div><div className="text-[10px] text-slate-500">RAM</div></div>
        <div className="p-2 rounded-lg bg-slate-900/50"><div className="text-sm font-semibold text-cyan-300">{s.plan?.vcore}</div><div className="text-[10px] text-slate-500">vCore</div></div>
        <div className="p-2 rounded-lg bg-slate-900/50"><div className="text-sm font-semibold text-cyan-300">{s.location?.code}</div><div className="text-[10px] text-slate-500">Lokasi</div></div>
      </div>
      {s.error && <p className="text-xs text-rose-400 mt-3 break-words">{s.error}</p>}
    </GlassCard>
  );
}
